import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus, Mail } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';
import { formatCurrency } from '@/lib/utils';

export interface InviteTicketOption {
    id: string;
    name: string;
    price: number;
}

interface InviteGuest {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    name: string; // Computed from firstName + lastName
    inviterPaysFor: boolean;
    inviteTicketId: string;
}

interface SendInvitesModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSendInvites: (guests: InviteGuest[], message: string) => Promise<void>;
    eventName: string;
    /** When the event uses tickets, one option per invitable ticket the host may use */
    inviteTicketOptions: InviteTicketOption[];
}

export default function SendInvitesModal({
    open,
    onOpenChange,
    onSendInvites,
    eventName,
    inviteTicketOptions,
}: SendInvitesModalProps) {
    const defaultTicketId = inviteTicketOptions[0]?.id ?? '';
    const [guests, setGuests] = useState<InviteGuest[]>([
        {
            id: '1',
            email: '',
            firstName: '',
            lastName: '',
            name: '',
            inviterPaysFor: true,
            inviteTicketId: defaultTicketId,
        },
    ]);
    const [message, setMessage] = useState(
        `You're invited to ${eventName}! Please click the link below to RSVP.`,
    );
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        const firstId = inviteTicketOptions[0]?.id ?? '';
        setGuests((prev) =>
            prev.map((g) => ({
                ...g,
                inviteTicketId:
                    g.inviteTicketId && inviteTicketOptions.some((o) => o.id === g.inviteTicketId)
                        ? g.inviteTicketId
                        : firstId,
            })),
        );
    }, [open, inviteTicketOptions]);

    const addGuest = () => {
        const firstId = inviteTicketOptions[0]?.id ?? '';
        setGuests([
            ...guests,
            {
                id: Date.now().toString(),
                email: '',
                firstName: '',
                lastName: '',
                name: '',
                inviterPaysFor: true,
                inviteTicketId: firstId,
            },
        ]);
    };

    const removeGuest = (id: string) => {
        if (guests.length > 1) {
            setGuests(guests.filter((g) => g.id !== id));
        }
    };

    const updateGuest = (id: string, field: keyof InviteGuest, value: string | boolean) => {
        setGuests((prev) =>
            prev.map((g) => {
                if (g.id === id) {
                    const updated = { ...g, [field]: value };
                    if (field === 'firstName' || field === 'lastName') {
                        const fn = field === 'firstName' ? String(value) : g.firstName;
                        const ln = field === 'lastName' ? String(value) : g.lastName;
                        updated.name = `${fn} ${ln}`.trim();
                    }
                    return updated;
                }
                return g;
            }),
        );
    };

    const handleSubmit = async () => {
        const invalidGuests = guests.filter((g) => !g.email || !g.firstName?.trim() || !g.lastName?.trim());
        if (invalidGuests.length > 0) {
            showError('Please fill in all email, first name, and last name fields');
            return;
        }

        if (inviteTicketOptions.length > 0) {
            const invalidTicket = guests.some(
                (g) =>
                    !g.inviteTicketId?.trim() ||
                    !inviteTicketOptions.some((o) => o.id === g.inviteTicketId),
            );
            if (invalidTicket) {
                showError('Please select an invitation ticket for each guest');
                return;
            }
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = guests.filter((g) => !emailRegex.test(g.email));
        if (invalidEmails.length > 0) {
            showError('Please enter valid email addresses');
            return;
        }

        setSending(true);
        try {
            await onSendInvites(guests, message);
            showSuccess(`Invitations sent to ${guests.length} guest${guests.length > 1 ? 's' : ''}!`);
            onOpenChange(false);
            const resetTicketId = inviteTicketOptions[0]?.id ?? '';
            setGuests([
                {
                    id: '1',
                    email: '',
                    firstName: '',
                    lastName: '',
                    name: '',
                    inviterPaysFor: true,
                    inviteTicketId: resetTicketId,
                },
            ]);
            setMessage(`You're invited to ${eventName}! Please click the link below to RSVP.`);
        } catch (error) {
            console.error('Error sending invites:', error);
            showError('Failed to send invitations. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Send Invitations
                    </DialogTitle>
                    <DialogDescription>
                        Send email invitations to guests. They will receive a link to accept or decline.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="message">Invitation Message</Label>
                        <Textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={3}
                            placeholder="Enter a personalized message for your invitees..."
                        />
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <Label>Guests to Invite</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addGuest} className="touch-manipulation">
                                <Plus className="mr-2 h-4 w-4" />
                                Add Guest
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {guests.map((guest) => (
                                <div key={guest.id} className="p-4 border rounded-lg space-y-3 relative">
                                    {guests.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute top-2 right-2"
                                            onClick={() => removeGuest(guest.id)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-2">
                                            <Label htmlFor={`firstName-${guest.id}`}>
                                                First Name <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id={`firstName-${guest.id}`}
                                                value={guest.firstName}
                                                onChange={(e) =>
                                                    updateGuest(guest.id, 'firstName', e.target.value)
                                                }
                                                placeholder="John"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor={`lastName-${guest.id}`}>
                                                Last Name <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id={`lastName-${guest.id}`}
                                                value={guest.lastName}
                                                onChange={(e) =>
                                                    updateGuest(guest.id, 'lastName', e.target.value)
                                                }
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor={`email-${guest.id}`}>
                                            Email <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id={`email-${guest.id}`}
                                            type="email"
                                            value={guest.email}
                                            onChange={(e) =>
                                                updateGuest(guest.id, 'email', e.target.value)
                                            }
                                            placeholder="john@example.com"
                                        />
                                    </div>

                                    {inviteTicketOptions.length > 0 && (
                                        <div className="space-y-2">
                                            <Label htmlFor={`invite-ticket-${guest.id}`}>
                                                Invitation ticket <span className="text-red-500">*</span>
                                            </Label>
                                            <Select
                                                value={guest.inviteTicketId || inviteTicketOptions[0]?.id}
                                                onValueChange={(value) =>
                                                    updateGuest(guest.id, 'inviteTicketId', value)
                                                }
                                            >
                                                <SelectTrigger id={`invite-ticket-${guest.id}`}>
                                                    <SelectValue placeholder="Select ticket" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {inviteTicketOptions.map((opt) => (
                                                        <SelectItem key={opt.id} value={opt.id}>
                                                            {opt.name} - {formatCurrency(opt.price || 0)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Guests may only RSVP for tickets you are allowed to share.
                                            </p>
                                        </div>
                                    )}

                                    <label
                                        htmlFor={`payment-${guest.id}`}
                                        className="flex cursor-pointer items-center gap-3 rounded-md py-2 touch-manipulation"
                                    >
                                        <input
                                            type="checkbox"
                                            id={`payment-${guest.id}`}
                                            checked={guest.inviterPaysFor}
                                            onChange={(e) =>
                                                updateGuest(guest.id, 'inviterPaysFor', e.target.checked)
                                            }
                                            className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">I will pay for this guest</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-2 justify-end pt-4 border-t">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleSubmit} disabled={sending}>
                            {sending ? (
                                <>
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Mail className="mr-2 h-4 w-4" />
                                    Send {guests.length} Invitation{guests.length > 1 ? 's' : ''}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
